<template>
  <div :class="containerClass">
    <page-header
      :title="this.$route.params.id ? 'Edit Resource' : 'Add Resource'"
      :breadcrumb="breadcrumb"
      :container-class="null"
      class="mb-32pt"
    />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <b-form-group
        :label="$t('schoolMsgs.school')"
        label-for="school"
        label-class="form-label"
        v-if="get(getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN"
      >
        <v-select
          id="school"
          class="form-control v-select-custom"
          label="name"
          v-model="school"
          :reduce="(school) => school.id"
          placeholder="Select School"
          :options="allSchools"
          :loading="areSchoolsLoading"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" :required="!school" v-bind="attributes" v-on="events" />
          </template>

          <template slot="option" slot-scope="option">
            <div class="d-flex align-items-center">
              <fmv-avatar :title="true" title-class="bg-transparent" rounded no-link slot="aside" size="xs">
                <b-img :src="option.logo_url" class="img-fluid" width="20" alt="Logo" v-if="option.logo_url" />
                <i class="fas fa-university fa-lg" v-else></i>
              </fmv-avatar>
              <span>{{ option.name }}</span>
            </div>
          </template>
          <template slot="selected-option" slot-scope="option">
            {{ option.name }}
          </template>
        </v-select>
        <b-form-invalid-feedback :state="!$v.school.required && $v.school.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <page-separator title="Resource Details" />

      <b-form-group label="Image" label-for="image" label-class="form-label" class="row-align-items-center">
        <b-media class="align-items-center" vertical-align="center">
          <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
            <span v-if="isLogoUploading">...</span>
            <b-img :src="resource.image_url" class="img-fluid" width="40" alt="Logo" v-else-if="resource.image_url" />
            <i class="material-icons" v-else>vignette</i>
          </fmv-avatar>

          <image-uploader
            @image="(e) => setImage(e, 'resource_images')"
            :isUploading="isLogoUploading"
            :isRequired="!resource.image_url"
            placeholder="Select resource's image"
          />
        </b-media>
      </b-form-group>

      <b-form-group label="Title" label-for="title" label-class="form-label">
        <b-form-input id="title" placeholder="Enter resource's title" v-model="resource.title" required />
      </b-form-group>

      <b-form-group label="Description" label-for="short-description" label-class="form-label">
        <b-form-textarea
          id="short-description"
          placeholder="Enter a description for the resource"
          :rows="5"
          v-model="resource.shortDesc"
          required
        />
      </b-form-group>

      <b-form-group label="CTA Button Text" label-for="external_text" label-class="form-label">
        <b-form-input id="external_text" placeholder="Enter button's text" v-model="resource.btnText" required />
      </b-form-group>

      <b-form-group label="CTA Button Link" label-for="external_link" label-class="form-label">
        <b-form-input
          id="external_link"
          placeholder="Enter button's link"
          v-model="resource.externalLink"
          :required="!resource.isEnabled"
        />
      </b-form-group>

      <div class="row">
        <div class="col-6">
          <b-form-group label-class="form-label">
            <b-form-checkbox id="visible" v-model="resource.isVisible" name="check-button" switch>
              Is Resource visible?
            </b-form-checkbox>
          </b-form-group>
        </div>
      </div>

      <b-btn variant="primary" :disabled="isFormLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? 'Update' : 'Post' }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
import axios from 'axios';
import { get } from 'lodash';
import { FmvAvatar } from 'fmv-avatar';

import { mapActions, mapGetters } from 'vuex';
import Page from '../../../components/Page.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { requiredIf } from 'vuelidate/lib/validators';
import { SCHOOL_PAYMENT_PLANS, USER_ROLES } from '../../../common/constants';
import vSelect from 'vue-select';
import ImageUploader from '@/components/ImageUploader.vue';

export default {
  components: { PageHeader, PageSeparator, FmvAvatar, vSelect, ImageUploader },
  extends: Page,

  data() {
    return {
      title: this.$route.params.id ? 'Edit Resource' : 'Add Resource',

      tinemceApiKey: process.env.VUE_APP_TINEMCE_API_KEY,

      resource: {
        school: null,
        image_url: '',
        title: '',
        shortDesc: '',
        description: '',
        isVisible: true,
        isEnabled: false,

        externalLink: '',
        btnText: '',
      },
      linkedWebsite: null,
      allSchools: [],
      areSchoolsLoading: false,

      isLoading: false,
      school: null,
      isLogoUploading: false,
      uploadFile: { resource_images: null },
      uploadPercent: { resource_images: 0 },
      uploadCancelTokenSource: { resource_images: null },
      SCHOOL_PAYMENT_PLANS,
      USER_ROLES,
    };
  },
  validations() {
    return {
      school: {
        required: requiredIf(() => get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN),
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        {
          text: 'Resources',
          to: {
            name: 'manage-resources',
          },
        },
        {
          text: this.$route.params.id ? 'Edit Resource' : 'Add Resource',
          active: true,
        },
      ];
    },
    isFormLoading() {
      return this.isLoading || this.isLogoUploading;
    },
  },

  methods: {
    ...mapActions('school', ['getAllSchools', 'getSchoolImageUploadPresignedUrl', 'getSchool']),
    ...mapActions('webConfig', ['createResource', 'updateResource', 'getResource', 'getLinkedImageUploadPresignedUrl']),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    get,
    setImage(file, image) {
      this.uploadFile[image] = file;
      this.uploadImage(image);
    },
    async onSubmit() {
      this.isLoading = true;
      this.$v.$touch();
      if (!this.$v.$invalid) {
        try {
          const data = {
            title: this.resource.title,
            image_url: this.resource.image_url,
            short_description: this.resource.shortDesc,
            description: this.resource.description,

            is_visible: this.resource.isVisible,
            enbl_detail_pg: this.resource.isEnabled,
            external_link: this.resource.externalLink,
            cta_btn_text: this.resource.btnText,
            school:
              get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN
                ? this.school
                : this.getLoggedInUser.linked_entity.id,
          };
          if (this.$route.params.id) {
            await this.updateResource({
              id: this.$route.params.id,
              data,
            });
            this.makeToast({ variant: 'success', msg: 'Resource Updated Successfully!' });
          } else {
            await this.createResource(data);
            this.makeToast({ variant: 'success', msg: 'Resource Added Successfully!' });
          }
          setTimeout(
            () =>
              this.$router.push({
                name: 'manage-resources',
                query: { school_id: this.school },
              }),
            250
          );
        } catch (_err) {
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        }
      } else {
        this.makeToast({ variant: 'danger', msg: 'Please fill all fields correctly.' });
      }
      this.isLoading = false;
    },
    async fetchSchools() {
      this.areSchoolsLoading = true;

      const response = await this.getAllSchools({
        sch_payment_plan__title: `${this.SCHOOL_PAYMENT_PLANS.ADVANCED},${this.SCHOOL_PAYMENT_PLANS.ADVANCED_AND_CHAT}`,
      });
      this.allSchools = response.data;

      this.areSchoolsLoading = false;
    },

    async uploadImage(uploadType) {
      this.uploadCancelTokenSource[uploadType] = axios.CancelToken.source();
      this.isLogoUploading = uploadType === 'resource_images';
      try {
        const urlResp = await this.getSchoolImageUploadPresignedUrl({
          file_name: this.uploadFile[uploadType].name,
          content_type: this.uploadFile[uploadType].type,
        });
        await this.uploadToPresignedUrl({
          url: urlResp.upload_url,
          file: this.uploadFile[uploadType],
          config: {
            onUploadProgress: function (progressEvent) {
              this.uploadPercent[uploadType] = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }.bind(this),
            cancelToken: this.uploadCancelTokenSource[uploadType].token,
          },
        });
        if (uploadType === 'resource_images') {
          this.resource.image_url = urlResp.upload_url.split('?')[0];
        }
      } catch (error) {
        this.uploadFile[uploadType] = null;
      }

      this.uploadCancelTokenSource[uploadType] = null;
      this.uploadPercent[uploadType] = 0;

      if (uploadType === 'resource_images') this.isLogoUploading = false;
    },
  },

  async mounted() {
    if (this.$route.query.school_id) {
      this.school = parseInt(this.$route.query.school_id);
    }
    if (get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN) {
      this.fetchSchools();
    }

    this.isLoading = true;
    try {
      if (this.$route.params.id) {
        const res = await this.getResource(this.$route.params.id);

        this.school = res.data.school;
        this.resource.image_url = res.data.image_url;
        this.resource.title = res.data.title;
        this.resource.shortDesc = res.data.short_description;
        this.resource.description = res.data.description;
        this.resource.isVisible = res.data.is_visible;

        this.resource.externalLink = res.data.external_link;
        this.resource.btnText = res.data.cta_btn_text;
      }
    } catch (e) {
      this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      this.$router.push({
        name: 'manage-resources',
      });
    }

    this.isLoading = false;
  },
};
</script>
